import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { PAYMENT_METHODS_INCASSO, PAYMENT_METHODS_USCITA } from '../constants/paymentMethods';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MultiSelect } from '../components/ui/multi-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Trash2, CreditCard, Banknote, Receipt, ArrowUpDown, ArrowUp, ArrowDown, Download, ExternalLink, TrendingUp, TrendingDown, DollarSign, Calculator, Filter, Calendar, Car, Home, Briefcase, FileText, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Metodi pagamento incassi cliente
const clientPaymentMethods = {
  contanti_drop: 'Contanti - Drop',
  pos_drop: 'POS - Drop',
  misto_drop: 'Misto - Drop',
  bonifico: 'Bonifico',
  bonifico_fornitore: 'Bonifico al Fornitore',
  da_incassare: 'Da Incassare',
  // Vecchi valori per retrocompatibilità
  contanti: 'Contanti',
  pos: 'POS',
  misto: 'Misto'
};

// Metodi pagamento uscite fornitori
const supplierPaymentMethods = {
  bonifico_posta: 'Bonifico - Posta',
  bonifico_drop: 'Bonifico - Drop',
  bonifico_cliente: 'Bonifico dal Cliente',
  paypal: 'PayPal',
  contanti: 'Contanti',
  da_saldare: 'Da Saldare'
};

const paymentStatusColors = {
  incassato: 'bg-green-100 text-green-700',
  da_incassare: 'bg-amber-100 text-amber-700',
  saldato: 'bg-green-100 text-green-700',
  da_saldare: 'bg-red-100 text-red-700'
};

const Payments = () => {
  const [activeTab, setActiveTab] = useState('incassi');
  const [payments, setPayments] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('incasso'); // incasso, uscita, provvigione
  const [editingPayment, setEditingPayment] = useState(null); // Payment being edited
  const [formData, setFormData] = useState({
    policy_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'contanti_drop',
    notes: '',
    type: 'incasso',
    // Provvigioni
    commission_in: '',
    commission_out: '',
    agent_id: '',
    agency_id: ''
  });
  
  // Filtri reportistica - ora supportano multi-select (array)
  const [filterAgent, setFilterAgent] = useState([]);
  const [filterAgency, setFilterAgency] = useState([]);
  const [filterCommissionStatus, setFilterCommissionStatus] = useState([]);
  const [filterPaymentMethod, setFilterPaymentMethod] = useState([]); // Nuovo filtro metodo pagamento
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [sortField, setSortField] = useState('payment_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination - Incassi
  const [currentPageIncassi, setCurrentPageIncassi] = useState(1);
  // Pagination - Uscite
  const [currentPageUscite, setCurrentPageUscite] = useState(1);
  // Pagination - Provvigioni
  const [currentPageProvvigioni, setCurrentPageProvvigioni] = useState(1);
  const ITEMS_PER_PAGE = 30;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, policiesRes, agenciesRes, agentSourcesRes] = await Promise.all([
        axios.get(`${API}/payments`),
        axios.get(`${API}/policies`),
        axios.get(`${API}/agencies`),
        axios.get(`${API}/agent-sources`)
      ]);
      setPayments(paymentsRes.data);
      setPolicies(policiesRes.data);
      setAgencies(agenciesRes.data);
      
      // Carica agenti/fonti dal database
      const agentSourceNames = agentSourcesRes.data.map(s => s.name).filter(Boolean);
      setAgents(agentSourceNames);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Helper per generare URL di navigazione (per link nativi <a>)
  const getClientUrl = (clientId) => `/clients?open=${clientId}`;
  
  const getAssetUrlFromPayment = (payment) => {
    const policy = policies.find(p => p.id === payment.policy_id);
    if (policy && policy.client_id && policy.asset_id) {
      return `/clients?open=${policy.client_id}&asset=${policy.asset_id}`;
    } else if (payment.client_id) {
      return `/clients?open=${payment.client_id}`;
    }
    return '#';
  };

  const getAssetIcon = (type) => {
    switch (type) {
      case 'auto': return Car;
      case 'casa': return Home;
      case 'attivita': return Briefcase;
      default: return FileText;
    }
  };

  // Trova info bene dalla polizza collegata
  const getAssetInfo = (payment) => {
    const policy = policies.find(p => p.id === payment.policy_id);
    if (policy) {
      return {
        type: policy.policy_type || 'auto',
        label: policy.asset_label || '-',
        client_id: policy.client_id,
        asset_id: policy.asset_id
      };
    }
    return { type: 'auto', label: '-', client_id: payment.client_id, asset_id: null };
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '€ 0,00';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-emerald-600" /> : 
      <ArrowDown className="w-4 h-4 text-emerald-600" />;
  };

  const openDialog = (type, paymentToEdit = null) => {
    setDialogType(type);
    setEditingPayment(paymentToEdit);
    
    if (paymentToEdit) {
      // Edit mode - pre-fill form with existing data
      setFormData({
        policy_id: paymentToEdit.policy_id || '',
        amount: paymentToEdit.amount?.toString() || '',
        payment_date: paymentToEdit.payment_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        payment_method: paymentToEdit.payment_method || (type === 'incasso' ? 'contanti_drop' : 'bonifico_drop'),
        notes: paymentToEdit.notes || '',
        type: paymentToEdit.type || type,
        commission_in: paymentToEdit.commission_in?.toString() || '',
        commission_out: paymentToEdit.commission_out?.toString() || '',
        agent_id: paymentToEdit.agent || '',
        agency_id: paymentToEdit.agency || ''
      });
    } else {
      // Create mode - empty form
      setFormData({
        policy_id: '',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: type === 'incasso' ? 'contanti_drop' : 'bonifico_drop',
        notes: '',
        type: type,
        commission_in: '',
        commission_out: '',
        agent_id: '',
        agency_id: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const policy = policies.find(p => p.id === formData.policy_id);
      const payload = {
        policy_id: formData.policy_id,
        client_id: policy?.client_id || '',
        amount: parseFloat(formData.amount) || 0,
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        notes: formData.notes,
        type: dialogType,
        commission_in: parseFloat(formData.commission_in) || 0,
        commission_out: parseFloat(formData.commission_out) || 0,
        agent: policy?.agent || formData.agent_id,
        agency: policy?.agent || formData.agency_id
      };
      
      if (editingPayment) {
        // Update existing payment
        await axios.put(`${API}/payments/${editingPayment.id}`, payload);
        toast.success('Movimento aggiornato con successo');
      } else {
        // Create new payment
        await axios.post(`${API}/payments`, payload);
        toast.success(dialogType === 'incasso' ? 'Incasso registrato' : 'Pagamento registrato');
      }
      
      setDialogOpen(false);
      setEditingPayment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo pagamento?')) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`);
      toast.success('Pagamento eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // Inline edit: aggiorna metodo di pagamento direttamente dalla tabella
  const handleUpdatePaymentMethod = async (paymentId, newMethod) => {
    try {
      await axios.patch(`${API}/payments/${paymentId}/method`, { payment_method: newMethod });
      // Aggiorna lo state locale per riflettere il cambiamento immediatamente
      setPayments(prev => prev.map(p => 
        p.id === paymentId ? { ...p, payment_method: newMethod } : p
      ));
      toast.success('Metodo aggiornato con successo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    }
  };

  // Filter payments - supporta multi-select (array) e ricerca per targa
  const filterPayments = (type) => {
    return payments
      .filter(p => {
        let matchesType;
        if (type === 'all') {
          matchesType = true;
        } else if (type === 'provvigioni') {
          matchesType = p.type === 'provvigione_entrata' || p.type === 'provvigione_uscita';
        } else {
          matchesType = p.type === type;
        }
        // Ricerca estesa: cliente, polizza, collaboratore E TARGA
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = searchTerm === '' || 
          p.client_name?.toLowerCase().includes(searchLower) ||
          p.policy_number?.toLowerCase().includes(searchLower) ||
          p.collaboratore?.toLowerCase().includes(searchLower) ||
          p.asset_targa?.toLowerCase().includes(searchLower) ||  // Ricerca per targa
          p.asset_label?.toLowerCase().includes(searchLower);    // Ricerca anche nel label (es. BMW GT554LB)
        
        // Filtri multi-select - se array vuoto, mostra tutti
        const matchesAgent = filterAgent.length === 0 || filterAgent.includes(p.client_agent_source);
        const matchesAgency = filterAgency.length === 0 || filterAgency.includes(p.agency);
        const matchesPaymentMethod = filterPaymentMethod.length === 0 || filterPaymentMethod.includes(p.payment_method);
        
        const matchesDateFrom = !filterDateFrom || new Date(p.payment_date) >= new Date(filterDateFrom);
        const matchesDateTo = !filterDateTo || new Date(p.payment_date) <= new Date(filterDateTo);
        
        // Filtro stato provvigioni - multi-select
        let matchesCommissionStatus = true;
        if (type === 'provvigioni' && filterCommissionStatus.length > 0) {
          matchesCommissionStatus = filterCommissionStatus.includes(p.commission_status);
        }
        
        return matchesType && matchesSearch && matchesAgent && matchesAgency && matchesPaymentMethod && matchesDateFrom && matchesDateTo && matchesCommissionStatus;
      })
      .sort((a, b) => {
        let aVal, bVal;
        if (sortField === 'payment_date') {
          aVal = new Date(a[sortField] || 0);
          bVal = new Date(b[sortField] || 0);
        } else if (sortField === 'amount') {
          aVal = a.amount || 0;
          bVal = b.amount || 0;
        } else {
          aVal = a[sortField];
          bVal = b[sortField];
        }
        if (sortDirection === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
      });
  };

  const incassi = filterPayments('incasso');
  const uscite = filterPayments('uscita');
  const provvigioni = filterPayments('provvigioni');
  const allFiltered = filterPayments('all');

  // Pagination logic for each tab
  const totalItemsIncassi = incassi.length;
  const totalPagesIncassi = Math.ceil(totalItemsIncassi / ITEMS_PER_PAGE);
  const startIndexIncassi = (currentPageIncassi - 1) * ITEMS_PER_PAGE;
  const endIndexIncassi = startIndexIncassi + ITEMS_PER_PAGE;
  const paginatedIncassi = incassi.slice(startIndexIncassi, endIndexIncassi);

  const totalItemsUscite = uscite.length;
  const totalPagesUscite = Math.ceil(totalItemsUscite / ITEMS_PER_PAGE);
  const startIndexUscite = (currentPageUscite - 1) * ITEMS_PER_PAGE;
  const endIndexUscite = startIndexUscite + ITEMS_PER_PAGE;
  const paginatedUscite = uscite.slice(startIndexUscite, endIndexUscite);

  const totalItemsProvvigioni = provvigioni.length;
  const totalPagesProvvigioni = Math.ceil(totalItemsProvvigioni / ITEMS_PER_PAGE);
  const startIndexProvvigioni = (currentPageProvvigioni - 1) * ITEMS_PER_PAGE;
  const endIndexProvvigioni = startIndexProvvigioni + ITEMS_PER_PAGE;
  const paginatedProvvigioni = provvigioni.slice(startIndexProvvigioni, endIndexProvvigioni);
  
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPageIncassi(1);
    setCurrentPageUscite(1);
    setCurrentPageProvvigioni(1);
  }, [payments.length, searchTerm, filterAgent, filterAgency, filterPaymentMethod, filterDateFrom, filterDateTo, filterCommissionStatus]);

  // Calcoli totali
  const totaleIncassi = incassi.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totaleDaIncassare = incassi.filter(p => p.payment_method === 'da_incassare').reduce((sum, p) => sum + (p.amount || 0), 0);
  const totaleUscite = uscite.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totaleDaSaldare = uscite.filter(p => p.payment_method === 'da_saldare').reduce((sum, p) => sum + (p.amount || 0), 0);
  const totaleProvvigioniIn = provvigioni.filter(p => p.type === 'provvigione_entrata').reduce((sum, p) => sum + (p.commission_in || 0), 0);
  const totaleProvvigioniOut = provvigioni.filter(p => p.type === 'provvigione_uscita').reduce((sum, p) => sum + (p.commission_out || 0), 0);

  const exportToCSV = () => {
    const headers = ['Data', 'Cliente', 'Polizza', 'Tipo', 'Metodo', 'Importo', 'Provv. Entrata', 'Provv. Uscita', 'Agente', 'Note'];
    const rows = allFiltered.map(p => [
      formatDate(p.payment_date),
      p.client_name || '',
      p.policy_number || '',
      p.type === 'incasso' ? 'Incasso' : 'Uscita',
      p.payment_method || '',
      p.amount?.toFixed(2) || '0',
      p.commission_in?.toFixed(2) || '0',
      p.commission_out?.toFixed(2) || '0',
      p.agent || '',
      p.notes || ''
    ]);
    const csvContent = [headers.join(';'), ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pagamenti_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Esportazione completata');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pagamenti & Provvigioni
          </h1>
          <p className="text-slate-600 mt-1">Gestisci incassi, pagamenti e provvigioni</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportToCSV} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Esporta
          </Button>
          <Button onClick={() => openDialog('incasso')} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Incasso
          </Button>
          <Button onClick={() => openDialog('uscita')} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Uscita
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-xs text-slate-500">Totale Incassi</p>
                <p className="text-lg font-bold text-green-700">{formatCurrency(totaleIncassi)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-xs text-slate-500">Da Incassare</p>
                <p className="text-lg font-bold text-amber-700">{formatCurrency(totaleDaIncassare)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-xs text-slate-500">Totale Uscite</p>
                <p className="text-lg font-bold text-red-700">{formatCurrency(totaleUscite)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-xs text-slate-500">Da Saldare</p>
                <p className="text-lg font-bold text-orange-700">{formatCurrency(totaleDaSaldare)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${(totaleIncassi - totaleUscite) >= 0 ? 'border-l-emerald-500' : 'border-l-rose-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Calculator className={`w-5 h-5 ${(totaleIncassi - totaleUscite) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`} />
              <div>
                <p className="text-xs text-slate-500">Margine Netto</p>
                <p className={`text-lg font-bold ${(totaleIncassi - totaleUscite) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {formatCurrency(totaleIncassi - totaleUscite)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per cliente, polizza o targa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <MultiSelect
              options={agents.map(a => ({ value: a, label: a }))}
              value={filterAgent}
              onValueChange={setFilterAgent}
              placeholder="Agente/Fonte"
              className="w-44"
            />
            <MultiSelect
              options={agencies.map(a => ({ value: a.name, label: a.name }))}
              value={filterAgency}
              onValueChange={setFilterAgency}
              placeholder="Agenzia"
              className="w-44"
            />
            <MultiSelect
              options={[
                { value: 'contanti_drop', label: 'Contanti - Drop' },
                { value: 'pos_drop', label: 'POS - Drop' },
                { value: 'misto_drop', label: 'Misto - Drop' },
                { value: 'bonifico', label: 'Bonifico' },
                { value: 'bonifico_fornitore', label: 'Bonifico al Fornitore' },
                { value: 'da_incassare', label: 'Da Incassare' },
                { value: 'bonifico_posta', label: 'Bonifico - Posta' },
                { value: 'bonifico_drop', label: 'Bonifico - Drop' },
                { value: 'bonifico_cliente', label: 'Bonifico dal Cliente' },
                { value: 'paypal', label: 'PayPal' },
                { value: 'contanti', label: 'Contanti' },
                { value: 'da_saldare', label: 'Da Saldare' },
              ]}
              value={filterPaymentMethod}
              onValueChange={setFilterPaymentMethod}
              placeholder="Metodo Pagamento"
              className="w-48"
            />
            <div className="flex gap-2">
              <DateInput
                value={filterDateFrom}
                onChange={(val) => setFilterDateFrom(val)}
                className="w-40"
                placeholder="Da"
              />
              <DateInput
                value={filterDateTo}
                onChange={(val) => setFilterDateTo(val)}
                className="w-40"
                placeholder="A"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="incassi" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Incassi ({incassi.length})
          </TabsTrigger>
          <TabsTrigger value="uscite" className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4" />
            Uscite Fornitori ({uscite.length})
          </TabsTrigger>
          <TabsTrigger value="provvigioni" className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Provvigioni ({provvigioni.length})
          </TabsTrigger>
          <TabsTrigger value="reportistica" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Reportistica
          </TabsTrigger>
        </TabsList>

        {/* Incassi Tab */}
        <TabsContent value="incassi">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-slate-500">Caricamento...</div>
              ) : incassi.length === 0 ? (
                <div className="p-8 text-center text-slate-500">Nessun incasso trovato</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="min-w-[100px]">
                        <button onClick={() => handleSort('payment_date')} className="flex items-center gap-1">
                          Data {getSortIcon('payment_date')}
                        </button>
                      </TableHead>
                      <TableHead className="min-w-[140px]">Cliente</TableHead>
                      <TableHead className="min-w-[140px]">Agenzia/Fornitore</TableHead>
                      <TableHead className="min-w-[180px]">Bene</TableHead>
                      <TableHead className="min-w-[120px]">Metodo</TableHead>
                      <TableHead className="min-w-[100px]">
                        <button onClick={() => handleSort('amount')} className="flex items-center gap-1">
                          Importo {getSortIcon('amount')}
                        </button>
                      </TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedIncassi.map(payment => {
                      const assetInfo = getAssetInfo(payment);
                      const Icon = getAssetIcon(assetInfo.type);
                      return (
                        <TableRow key={payment.id}>
                          <TableCell>{formatDate(payment.payment_date)}</TableCell>
                          <TableCell className="font-medium text-slate-700">{payment.client_name || '-'}</TableCell>
                          <TableCell className="font-medium text-slate-700">{payment.agency || '-'}</TableCell>
                          <TableCell className="min-w-[180px]">
                            <Link
                              to={getAssetUrlFromPayment(payment)}
                              className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group w-full no-underline"
                            >
                              <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${
                                assetInfo.type === 'auto' ? 'bg-blue-100 text-blue-600' :
                                assetInfo.type === 'casa' ? 'bg-green-100 text-green-600' :
                                'bg-purple-100 text-purple-600'
                              }`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 truncate">
                                {assetInfo.label}
                              </span>
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={payment.payment_method} 
                              onValueChange={(value) => handleUpdatePaymentMethod(payment.id, value)}
                            >
                              <SelectTrigger className={`w-40 h-8 text-xs ${payment.payment_method === 'da_incassare' ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PAYMENT_METHODS_INCASSO.map(method => (
                                  <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="font-medium text-green-700">{formatCurrency(payment.amount)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => openDialog('incasso', payment)}
                                title="Modifica movimento"
                                data-testid={`edit-payment-${payment.id}`}
                              >
                                <Pencil className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(payment.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              
              {/* Pagination - Incassi */}
              {incassi.length > 0 && totalPagesIncassi > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Visualizzando {startIndexIncassi + 1}-{Math.min(endIndexIncassi, totalItemsIncassi)} di {totalItemsIncassi} incassi
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPageIncassi(p => Math.max(1, p - 1))}
                      disabled={currentPageIncassi === 1}
                      className="h-8"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Precedente
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPagesIncassi }, (_, i) => i + 1)
                        .filter(page => page === 1 || page === totalPagesIncassi || Math.abs(page - currentPageIncassi) <= 1)
                        .map((page, idx, arr) => {
                          const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                          return (
                            <div key={page} className="flex items-center">
                              {showEllipsis && <span className="px-2 text-slate-400">...</span>}
                              <Button
                                variant={currentPageIncassi === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPageIncassi(page)}
                                className={`h-8 w-8 p-0 ${currentPageIncassi === page ? 'bg-slate-900' : ''}`}
                              >{page}</Button>
                            </div>
                          );
                        })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPageIncassi(p => Math.min(totalPagesIncassi, p + 1))}
                      disabled={currentPageIncassi === totalPagesIncassi}
                      className="h-8"
                    >
                      Successiva
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              {totalPagesIncassi === 1 && incassi.length > 0 && (
                <div className="text-sm text-slate-500 text-center py-2 border-t border-slate-200">
                  Visualizzando {totalItemsIncassi} incass{totalItemsIncassi === 1 ? 'o' : 'i'}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Uscite Tab */}
        <TabsContent value="uscite">
          <Card>
            <CardContent className="p-0">
              {uscite.length === 0 ? (
                <div className="p-8 text-center text-slate-500">Nessuna uscita trovata</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="min-w-[100px]">Data</TableHead>
                      <TableHead className="min-w-[140px]">Cliente</TableHead>
                      <TableHead className="min-w-[150px]">Agenzia/Fornitore</TableHead>
                      <TableHead className="min-w-[200px]">Bene</TableHead>
                      <TableHead className="min-w-[120px]">Metodo</TableHead>
                      <TableHead className="min-w-[100px]">Importo</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedUscite.map(payment => {
                      const assetInfo = getAssetInfo(payment);
                      const Icon = getAssetIcon(assetInfo.type);
                      return (
                        <TableRow key={payment.id}>
                          <TableCell>{formatDate(payment.payment_date)}</TableCell>
                          <TableCell className="font-medium text-slate-700">{payment.client_name || '-'}</TableCell>
                          <TableCell className="font-medium">{payment.agency || '-'}</TableCell>
                          <TableCell className="min-w-[200px]">
                            <Link
                              to={getAssetUrlFromPayment(payment)}
                              className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group w-full"
                            >
                              <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${
                                assetInfo.type === 'auto' ? 'bg-blue-100 text-blue-600' :
                                assetInfo.type === 'casa' ? 'bg-green-100 text-green-600' :
                                'bg-purple-100 text-purple-600'
                              }`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 truncate">
                                {assetInfo.label}
                              </span>
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={payment.payment_method} 
                              onValueChange={(value) => handleUpdatePaymentMethod(payment.id, value)}
                            >
                              <SelectTrigger className={`w-40 h-8 text-xs ${payment.payment_method === 'da_saldare' ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PAYMENT_METHODS_USCITA.map(method => (
                                  <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="font-medium text-red-700">{formatCurrency(payment.amount)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => openDialog('uscita', payment)}
                                title="Modifica movimento"
                                data-testid={`edit-payment-${payment.id}`}
                              >
                                <Pencil className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(payment.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              
              {/* Pagination - Uscite */}
              {uscite.length > 0 && totalPagesUscite > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Visualizzando {startIndexUscite + 1}-{Math.min(endIndexUscite, totalItemsUscite)} di {totalItemsUscite} uscite
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPageUscite(p => Math.max(1, p - 1))}
                      disabled={currentPageUscite === 1}
                      className="h-8"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Precedente
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPagesUscite }, (_, i) => i + 1)
                        .filter(page => page === 1 || page === totalPagesUscite || Math.abs(page - currentPageUscite) <= 1)
                        .map((page, idx, arr) => {
                          const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                          return (
                            <div key={page} className="flex items-center">
                              {showEllipsis && <span className="px-2 text-slate-400">...</span>}
                              <Button
                                variant={currentPageUscite === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPageUscite(page)}
                                className={`h-8 w-8 p-0 ${currentPageUscite === page ? 'bg-slate-900' : ''}`}
                              >{page}</Button>
                            </div>
                          );
                        })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPageUscite(p => Math.min(totalPagesUscite, p + 1))}
                      disabled={currentPageUscite === totalPagesUscite}
                      className="h-8"
                    >
                      Successiva
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              {totalPagesUscite === 1 && uscite.length > 0 && (
                <div className="text-sm text-slate-500 text-center py-2 border-t border-slate-200">
                  Visualizzando {totalItemsUscite} uscit{totalItemsUscite === 1 ? 'a' : 'e'}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Provvigioni Tab */}
        <TabsContent value="provvigioni">
          {/* Filtri Provvigioni */}
          <Card className="mb-4 border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <MultiSelect
                  options={[
                    { value: 'da_definire', label: 'Da Definire' },
                    { value: 'incassato', label: 'Incassato' },
                    { value: 'saldato', label: 'Saldato' },
                  ]}
                  value={filterCommissionStatus}
                  onValueChange={setFilterCommissionStatus}
                  placeholder="Stato"
                  className="w-40"
                />
                <MultiSelect
                  options={agencies.map(a => ({ value: a.name, label: a.name }))}
                  value={filterAgency}
                  onValueChange={setFilterAgency}
                  placeholder="Agenzia (Entrate)"
                  className="w-48"
                />
                <MultiSelect
                  options={agents.map(a => ({ value: a, label: a }))}
                  value={filterAgent}
                  onValueChange={setFilterAgent}
                  placeholder="Agente (Uscite)"
                  className="w-48"
                />
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>Totali filtrati:</span>
                  <span className="text-cyan-600 font-medium">Entrata: {formatCurrency(totaleProvvigioniIn)}</span>
                  <span className="text-purple-600 font-medium">Uscita: {formatCurrency(totaleProvvigioniOut)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {provvigioni.length === 0 ? (
                <div className="p-8 text-center text-slate-500">Nessuna provvigione trovata</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="min-w-[100px]">
                        <button onClick={() => handleSort('payment_date')} className="flex items-center gap-1">
                          Data {getSortIcon('payment_date')}
                        </button>
                      </TableHead>
                      <TableHead className="min-w-[80px]">Tipo</TableHead>
                      <TableHead className="min-w-[100px]">Stato</TableHead>
                      <TableHead className="min-w-[140px]">Agenzia/Agente</TableHead>
                      <TableHead className="min-w-[140px]">Cliente</TableHead>
                      <TableHead className="min-w-[160px]">Bene</TableHead>
                      <TableHead className="min-w-[100px]">Importo</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProvvigioni.map(payment => {
                      const assetInfo = getAssetInfo(payment);
                      const Icon = getAssetIcon(assetInfo.type);
                      const isEntrata = payment.type === 'provvigione_entrata';
                      const statusLabel = payment.commission_status === 'da_definire' ? 'Da Definire' :
                                         payment.commission_status === 'incassato' ? 'Incassato' :
                                         payment.commission_status === 'saldato' ? 'Saldato' : payment.commission_status || 'Da Definire';
                      const statusColor = payment.commission_status === 'da_definire' ? 'bg-amber-100 text-amber-700' :
                                         payment.commission_status === 'incassato' ? 'bg-green-100 text-green-700' :
                                         payment.commission_status === 'saldato' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700';
                      // Per entrata mostra Agenzia, per uscita mostra Collaboratore/Agente
                      const collegamento = isEntrata ? payment.agency : payment.collaboratore;
                      return (
                        <TableRow key={payment.id}>
                          <TableCell>{formatDate(payment.payment_date)}</TableCell>
                          <TableCell>
                            <Badge className={isEntrata ? 'bg-cyan-100 text-cyan-700' : 'bg-purple-100 text-purple-700'}>
                              {isEntrata ? 'Entrata' : 'Uscita'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColor}>
                              {statusLabel}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className={`font-medium text-sm ${isEntrata ? 'text-cyan-700' : 'text-purple-700'}`}>
                              {collegamento || '-'}
                            </span>
                            <p className="text-xs text-slate-400">{isEntrata ? 'Agenzia' : 'Agente'}</p>
                          </TableCell>
                          <TableCell>
                            <Link to={getClientUrl(payment.client_id)} className="text-blue-600 hover:underline flex items-center gap-1">
                              {payment.client_name} <ExternalLink className="w-3 h-3" />
                            </Link>
                          </TableCell>
                          <TableCell className="min-w-[160px]">
                            <Link
                              to={getAssetUrlFromPayment(payment)}
                              className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group w-full"
                            >
                              <div className={`w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center ${
                                assetInfo.type === 'auto' ? 'bg-blue-100 text-blue-600' :
                                assetInfo.type === 'casa' ? 'bg-green-100 text-green-600' :
                                'bg-purple-100 text-purple-600'
                              }`}>
                                <Icon className="w-3.5 h-3.5" />
                              </div>
                              <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 truncate">
                                {assetInfo.label}
                              </span>
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </Link>
                          </TableCell>
                          <TableCell className={`font-medium ${isEntrata ? 'text-cyan-700' : 'text-purple-700'}`}>
                            {formatCurrency(isEntrata ? payment.commission_in : payment.commission_out)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => openDialog('provvigione', payment)}
                                title="Modifica provvigione"
                                data-testid={`edit-payment-${payment.id}`}
                              >
                                <Pencil className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(payment.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              
              {/* Pagination - Provvigioni */}
              {provvigioni.length > 0 && totalPagesProvvigioni > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Visualizzando {startIndexProvvigioni + 1}-{Math.min(endIndexProvvigioni, totalItemsProvvigioni)} di {totalItemsProvvigioni} provvigioni
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPageProvvigioni(p => Math.max(1, p - 1))}
                      disabled={currentPageProvvigioni === 1}
                      className="h-8"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Precedente
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPagesProvvigioni }, (_, i) => i + 1)
                        .filter(page => page === 1 || page === totalPagesProvvigioni || Math.abs(page - currentPageProvvigioni) <= 1)
                        .map((page, idx, arr) => {
                          const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                          return (
                            <div key={page} className="flex items-center">
                              {showEllipsis && <span className="px-2 text-slate-400">...</span>}
                              <Button
                                variant={currentPageProvvigioni === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPageProvvigioni(page)}
                                className={`h-8 w-8 p-0 ${currentPageProvvigioni === page ? 'bg-slate-900' : ''}`}
                              >{page}</Button>
                            </div>
                          );
                        })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPageProvvigioni(p => Math.min(totalPagesProvvigioni, p + 1))}
                      disabled={currentPageProvvigioni === totalPagesProvvigioni}
                      className="h-8"
                    >
                      Successiva
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              {totalPagesProvvigioni === 1 && provvigioni.length > 0 && (
                <div className="text-sm text-slate-500 text-center py-2 border-t border-slate-200">
                  Visualizzando {totalItemsProvvigioni} provvigion{totalItemsProvvigioni === 1 ? 'e' : 'i'}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reportistica Tab */}
        <TabsContent value="reportistica">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Riepilogo per Agente/Fonte */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Provvigioni per Agente/Fonte</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {agents.map(agent => {
                    const agentPayments = provvigioni.filter(p => p.client_agent_source === agent);
                    const totIn = agentPayments.filter(p => p.type === 'provvigione_entrata').reduce((sum, p) => sum + (p.commission_in || 0), 0);
                    const totOut = agentPayments.filter(p => p.type === 'provvigione_uscita').reduce((sum, p) => sum + (p.commission_out || 0), 0);
                    return (
                      <div key={agent} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium">{agent}</span>
                        <div className="flex gap-4 text-sm">
                          <span className="text-cyan-600">Entrata: {formatCurrency(totIn)}</span>
                          <span className="text-purple-600">Uscita: {formatCurrency(totOut)}</span>
                        </div>
                      </div>
                    );
                  })}
                  {agents.length === 0 && (
                    <p className="text-center text-slate-500 py-4">Nessun agente trovato</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Riepilogo Totale Periodo */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Riepilogo Periodo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-cyan-50 rounded-lg">
                    <p className="text-sm text-cyan-700">Totale Provvigioni in Entrata</p>
                    <p className="text-2xl font-bold text-cyan-800">{formatCurrency(totaleProvvigioniIn)}</p>
                    <p className="text-xs text-cyan-600 mt-1">Margine Agenzia nel periodo filtrato</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-700">Totale Provvigioni in Uscita</p>
                    <p className="text-2xl font-bold text-purple-800">{formatCurrency(totaleProvvigioniOut)}</p>
                    <p className="text-xs text-purple-600 mt-1">Spettanze Agenti nel periodo filtrato</p>
                  </div>
                  <div className="p-4 bg-slate-100 rounded-lg">
                    <p className="text-sm text-slate-700">Margine Netto</p>
                    <p className={`text-2xl font-bold ${totaleProvvigioniIn - totaleProvvigioniOut >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                      {formatCurrency(totaleProvvigioniIn - totaleProvvigioniOut)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingPayment(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dialogType === 'incasso' ? (
                <><TrendingUp className="w-5 h-5 text-green-600" /> {editingPayment ? 'Modifica Incasso' : 'Nuovo Incasso'}</>
              ) : dialogType === 'provvigione' ? (
                <><Calculator className="w-5 h-5 text-purple-600" /> {editingPayment ? 'Modifica Provvigione' : 'Nuova Provvigione'}</>
              ) : (
                <><TrendingDown className="w-5 h-5 text-red-600" /> {editingPayment ? 'Modifica Uscita' : 'Nuova Uscita'}</>
              )}
            </DialogTitle>
            <DialogDescription>
              {editingPayment ? 'Modifica i dati del movimento selezionato' : 
                (dialogType === 'incasso' ? 'Registra un incasso da cliente' : 'Registra un pagamento a fornitore/agenzia')}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Polizza *</Label>
              <Select value={formData.policy_id} onValueChange={(v) => setFormData({...formData, policy_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona polizza" />
                </SelectTrigger>
                <SelectContent>
                  {policies.map(policy => (
                    <SelectItem key={policy.id} value={policy.id}>
                      {policy.policy_number} - {policy.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importo (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Data *</Label>
                <DateInput
                  value={formData.payment_date}
                  onChange={(val) => setFormData({...formData, payment_date: val})}
                  placeholder="gg/mm/aaaa"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Metodo Pagamento *</Label>
              <Select value={formData.payment_method} onValueChange={(v) => setFormData({...formData, payment_method: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dialogType === 'incasso' ? (
                    <>
                      <SelectItem value="_header_incasso" disabled className="font-bold text-slate-500 bg-slate-100">--- SEZIONE INCASSO ---</SelectItem>
                      {PAYMENT_METHODS_INCASSO.map(method => (
                        <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                      ))}
                    </>
                  ) : (
                    <>
                      <SelectItem value="_header_uscita" disabled className="font-bold text-slate-500 bg-slate-100">--- SEZIONE USCITA ---</SelectItem>
                      {PAYMENT_METHODS_USCITA.map(method => (
                        <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Provvigione Entrata (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.commission_in}
                  onChange={(e) => setFormData({...formData, commission_in: e.target.value})}
                  placeholder="Margine Agenzia"
                />
              </div>
              <div className="space-y-2">
                <Label>Provvigione Uscita (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.commission_out}
                  onChange={(e) => setFormData({...formData, commission_out: e.target.value})}
                  placeholder="Spettanza Agente"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingPayment(null); }}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                className={
                  dialogType === 'incasso' ? 'bg-green-600 hover:bg-green-700' : 
                  dialogType === 'provvigione' ? 'bg-purple-600 hover:bg-purple-700' :
                  'bg-red-600 hover:bg-red-700'
                }
              >
                {editingPayment ? 'Salva Modifiche' : (dialogType === 'incasso' ? 'Registra Incasso' : dialogType === 'provvigione' ? 'Registra Provvigione' : 'Registra Uscita')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;
